import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import type { WalletSessionValue } from "@tenergy/web-core/wallet";
import { adminRequest } from "../api/request.js";
import { roleAtLeast, type AdminIdentity } from "../api/types.js";
import { SHOW_LEDGER_TRON } from "../auth/ledger-flags.js";
import { addPasskey } from "../auth/sign-in-flow.js";
import { utc } from "../format.js";
import { loadLedger } from "../session/use-admin-session.js";
import { toastError, toastSuccess } from "../toast.js";
import { Blank, Button, ErrorNote, Fact, Note, Panel, Pill, ScreenHeader, Spinner, Table, Td, Th, inputClass } from "../ui.js";

/**
 * Security (docs/product/admin-auth.md §4, §5, §6): this admin's sessions, passkeys and confirmation key.
 *
 *   sessions        live ones, own; an admin and root see everyone's and can end them (root: any)
 *   passkeys        added only from a session opened by a wallet signature; a passkey equals that signature
 *   confirm key     the key that signs dangerous actions. Changing it is itself confirmed by the CURRENT key, so a
 *                   taken session cannot swap it. Root's is set in the API environment.
 */

interface SessionRow {
  id: string;
  address: string;
  method: string;
  is_root: boolean;
  is_current: boolean;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  ip: string | null;
  user_agent: string | null;
}

interface PasskeyRow {
  id: string;
  label: string | null;
  created_at: string;
  last_used_at: string | null;
}

interface ConfirmKey {
  key_address: string;
  kind: "tron" | "ethereum";
  from_env: boolean;
  is_sign_in_address: boolean;
  safest: boolean;
}

const METHOD_LABEL: Record<string, string> = { tron: "TRON signature", ethereum: "Ethereum signature", passkey: "Passkey" };

export function SecurityScreen({ identity, wallet }: { identity: AdminIdentity; wallet: WalletSessionValue }) {
  return (
    <>
      <ScreenHeader
        title="Security"
        lead="Your sessions, passkeys and the key that confirms dangerous actions. A session ends after 24 hours without use and after 28 days in any case."
      />
      <div class="grid grid-cols-[minmax(0,1fr)] gap-3">
        <ConfirmKeyPanel identity={identity} wallet={wallet} />
        <PasskeysPanel identity={identity} />
        <SessionsPanel identity={identity} />
      </div>
    </>
  );
}

function ConfirmKeyPanel({ identity, wallet }: { identity: AdminIdentity; wallet: WalletSessionValue }) {
  const key = useResource((signal) => adminRequest<ConfirmKey>("/admin/confirm-key", { signal }), []);
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** The new key signs the server's text first (proving it is held), then the current key confirms the change. */
  async function change(how: "ledger-ethereum" | "ledger-tron" | "wallet", transport: "usb" | "ble" = "usb") {
    setBusy(true);
    setError(null);
    try {
      let keyAddress = next.trim();
      let sign: (text: string) => Promise<string>;
      if (how === "wallet") {
        keyAddress = keyAddress || (wallet.address ?? (await wallet.requestConnection()));
        sign = wallet.signMessage;
      } else {
        const ledger = await loadLedger();
        const app = how === "ledger-ethereum" ? "ethereum" : "tron";
        // The typed address is found among the device's accounts; blank takes Ledger Live account 1.
        const { link, account } = await ledger.withLedger(transport, async (link) => ({
          link,
          account: keyAddress !== "" ? await ledger.findLedgerAccount(link, app, keyAddress) : (await ledger.ledgerAccounts(link, app, 0, 1))[0]!,
        }));
        keyAddress = account.address;
        sign = (text) => ledger.ledgerSignMessage(link, account, text);
      }
      const challenge = await adminRequest<{ challenge_id: string; message: string }>("/admin/confirm-key/challenge", {
        method: "POST",
        body: { key_address: keyAddress },
      });
      const signature = await sign(challenge.message);
      await adminRequest("/admin/confirm-key", {
        method: "PUT",
        body: { challenge_id: challenge.challenge_id, signature, key_address: keyAddress },
      });
      toastSuccess("Confirmation key changed");
      setNext("");
      key.reload();
    } catch (failure) {
      const ledger = await loadLedger().catch(() => null);
      setError(ledger?.describeLedgerError(failure) ?? (failure instanceof Error ? failure.message : "Not changed."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Confirmation key" subtitle="Signs every dangerous action: prices, top-ups, large withdrawals, admin roles, B2B contracts.">
      {key.loading ? (
        <Spinner />
      ) : key.error !== null ? (
        <ErrorNote error={key.error} onRetry={() => key.reload()} />
      ) : key.data === null ? null : (
        <div class="space-y-3">
          <dl class="max-w-xl">
            <Fact label="Key" value={key.data.key_address} mono />
            <Fact label="Kind" value={key.data.kind === "ethereum" ? "Ethereum" : "TRON"} />
            <Fact label="Set by" value={key.data.from_env ? "API environment (root)" : key.data.is_sign_in_address ? "Your sign-in address" : "Bound by you"} />
          </dl>
          {key.data.safest ? (
            <Note>Safest: with the Ledger Ethereum app, the device shows each confirmation's text before you approve.</Note>
          ) : (
            <Note tone="warning">
              Less safe: a TRON key shows only a hash on a Ledger, and a browser wallet signs on this computer. The safest
              key is a Ledger with the Ethereum app.
            </Note>
          )}
          {key.data.from_env ? null : identity.method === "passkey" ? (
            <p class="text-[11px] text-ink-muted">To change the key, sign in with your wallet, not a passkey.</p>
          ) : (
            <div class="space-y-2">
              <p class="text-[11.5px] text-ink-muted">
                Change it: the new key signs once to prove you hold it, then the current key confirms the change.
              </p>
              <input
                class={`${inputClass} num`}
                aria-label="New key address"
                placeholder="New key address, 0x… or T… (blank: Ledger account 1, or the connected wallet)"
                value={next}
                onInput={(event) => setNext((event.currentTarget as HTMLInputElement).value)}
                spellcheck={false}
              />
              <div class="flex flex-wrap gap-2">
                <Button disabled={busy} onClick={() => void change("ledger-ethereum", "usb")}>
                  Ledger Ethereum · USB
                </Button>
                <Button tone="ghost" disabled={busy} onClick={() => void change("ledger-ethereum", "ble")}>
                  Ledger Ethereum · Bluetooth
                </Button>
                {SHOW_LEDGER_TRON ? (
                  <Button tone="ghost" disabled={busy} onClick={() => void change("ledger-tron", "usb")}>
                    Ledger TRON · USB
                  </Button>
                ) : null}
                <Button tone="ghost" disabled={busy} onClick={() => void change("wallet")}>
                  TRON wallet
                </Button>
              </div>
              {error === null ? null : <Note tone="danger">{error}</Note>}
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

function PasskeysPanel({ identity }: { identity: AdminIdentity }) {
  const list = useResource((signal) => adminRequest<{ data: PasskeyRow[] }>("/admin/passkeys", { signal }), []);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const rows = list.data?.data ?? [];

  async function add() {
    setBusy(true);
    try {
      await addPasskey(label.trim() || "Security key");
      toastSuccess("Passkey added");
      setLabel("");
      list.reload();
    } catch (error) {
      toastError("Passkey not added", error);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    try {
      await adminRequest(`/admin/passkeys/${encodeURIComponent(id)}`, { method: "DELETE" });
      toastSuccess("Passkey removed");
      list.reload();
    } catch (error) {
      toastError("Passkey not removed", error);
    }
  }

  return (
    <Panel
      title="Passkeys"
      subtitle="A security key bound to your address signs you in like your wallet does. Ledger: open the Security Key app and connect by USB; over NFC, Windows can hang on the second tap."
    >
      {identity.method === "passkey" ? (
        <p class="mb-3 text-[11px] text-ink-muted">To add a passkey, sign in with your wallet.</p>
      ) : (
        <div class="mb-3 flex flex-wrap gap-2">
          <input
            class={`${inputClass} min-w-0 flex-1`}
            aria-label="Passkey label"
            placeholder="Label, e.g. Ledger Flex"
            maxLength={60}
            value={label}
            onInput={(event) => setLabel((event.currentTarget as HTMLInputElement).value)}
          />
          <Button disabled={busy} onClick={() => void add()}>
            Add passkey
          </Button>
        </div>
      )}
      {list.loading ? (
        <Spinner />
      ) : list.error !== null ? (
        <ErrorNote error={list.error} onRetry={() => list.reload()} />
      ) : (
        <Table
          rows={rows.length}
          empty="No passkey yet."
          head={
            <tr>
              <Th>Label</Th>
              <Th>Added</Th>
              <Th>Last used</Th>
              <Th>{""}</Th>
            </tr>
          }
        >
          {rows.map((row) => (
            <tr key={row.id}>
              <Td>{row.label ?? <Blank />}</Td>
              <Td mono>{utc(row.created_at)}</Td>
              <Td mono>{row.last_used_at === null ? <Blank title="Never used" /> : utc(row.last_used_at)}</Td>
              <Td>
                <Button tone="danger" onClick={() => void remove(row.id)}>
                  Remove
                </Button>
              </Td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}

function SessionsPanel({ identity }: { identity: AdminIdentity }) {
  const list = useResource((signal) => adminRequest<{ data: SessionRow[] }>("/admin/sessions", { signal }), []);
  const rows = list.data?.data ?? [];
  const everyone = roleAtLeast(identity.role, "admin");

  async function end(id: string) {
    try {
      await adminRequest(`/admin/sessions/${encodeURIComponent(id)}`, { method: "DELETE" });
      toastSuccess("Session ended");
      list.reload();
    } catch (error) {
      toastError("Session not ended", error);
    }
  }

  return (
    <Panel
      title="Sessions"
      subtitle={everyone ? "Every live admin session. You can end any you manage." : "Your live sessions. End one you do not recognise."}
    >
      {list.loading ? (
        <Spinner />
      ) : list.error !== null ? (
        <ErrorNote error={list.error} onRetry={() => list.reload()} />
      ) : (
        <Table
          rows={rows.length}
          empty="No live session."
          head={
            <tr>
              {everyone ? <Th>Address</Th> : null}
              <Th>Signed in by</Th>
              <Th>Started</Th>
              <Th>Last used</Th>
              <Th>IP</Th>
              <Th>Browser</Th>
              <Th>{""}</Th>
            </tr>
          }
        >
          {rows.map((row) => (
            <tr key={row.id}>
              {everyone ? (
                <Td mono>
                  {row.address}
                  {row.is_root ? " · root" : ""}
                </Td>
              ) : null}
              <Td>{METHOD_LABEL[row.method] ?? row.method}</Td>
              <Td mono>{utc(row.created_at)}</Td>
              <Td mono>{utc(row.last_seen_at)}</Td>
              <Td mono>{row.ip ?? <Blank />}</Td>
              <Td>
                <span class="block max-w-[260px] truncate" title={row.user_agent ?? ""}>
                  {row.user_agent ?? <Blank />}
                </span>
              </Td>
              <Td>
                {row.is_current ? (
                  <Pill value="this one" tone="good" />
                ) : (
                  <Button tone="danger" onClick={() => void end(row.id)}>
                    End
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}
